import React, { useState, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { apiPost } from '../services/apiClient';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SwitchingReasonsHistogram = ({
    topicId,
    startDate,
    endDate,
    highlightedPeriod,
    className = '',
    style = {}
}) => {
    const [reasonsData, setReasonsData] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('both'); // 'leaving', 'joining', 'both'

    // Fetch motivation data from post_motivations table
    useEffect(() => {
        if (!topicId) {
            setChartData(null);
            setReasonsData([]);
            return;
        }

        const fetchMotivationData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Calculate timestamps for the function
                const startTimestamp = startDate ? Math.floor(startDate.getTime() / 1000) : 0;
                const endTimestamp = endDate ? Math.floor(endDate.getTime() / 1000) : Math.floor(Date.now() / 1000);

                console.log('🔍 SWITCHING REASONS: Fetching motivation data for topic:', topicId);
                console.log('🔍 SWITCHING REASONS: Selected type:', selectedType);
                console.log('🔍 SWITCHING REASONS: Date range:', {
                    startDate: startDate?.toISOString(),
                    endDate: endDate?.toISOString()
                });
                console.log('🔍 SWITCHING REASONS: Function parameters will be:', {
                    input_topic_id: topicId,
                    input_start_time: startTimestamp,
                    input_end_time: endTimestamp
                });

                // Fetch data based on selected type
                const dataPromises = [];

                if (selectedType === 'leaving' || selectedType === 'both') {
                    dataPromises.push(
                        apiPost('topic/motivation/histogram', {
                            topic_id: topicId,
                            type: 'leaving',
                            start_time: startTimestamp,
                            end_time: endTimestamp,
                        }).then(async (response) => {
                            if (!response.ok) {
                                throw new Error(`Backend error: ${response.status}`);
                            }
                            const json = await response.json();
                            return { data: json.data ?? json ?? [], type: 'leaving' };
                        })
                    );
                }

                if (selectedType === 'joining' || selectedType === 'both') {
                    dataPromises.push(
                        apiPost('topic/motivation/histogram', {
                            topic_id: topicId,
                            type: 'joining',
                            start_time: startTimestamp,
                            end_time: endTimestamp,
                        }).then(async (response) => {
                            if (!response.ok) {
                                throw new Error(`Backend error: ${response.status}`);
                            }
                            const json = await response.json();
                            return { data: json.data ?? json ?? [], type: 'joining' };
                        })
                    );
                }

                const results = await Promise.all(dataPromises);

                // Process results
                const processedData = {
                    leaving: [],
                    joining: [],
                    totalPosts: 0
                };

                results.forEach(result => {
                    console.log(`🔍 SWITCHING REASONS: Received ${result.type} data:`, result.data?.length || 0, 'items');
                    if (result.data && result.data.length > 0) {
                        console.log(`🔍 SWITCHING REASONS: Sample ${result.type} data:`, result.data.slice(0, 3));
                        processedData[result.type] = result.data;
                        // Get total_posts from the first item (should be the same for all items)
                        if (result.data[0]?.total_posts) {
                            // For 'both' mode, leaving and joining should have the same total_posts
                            // so we only want to count it once
                            if (selectedType === 'both' && processedData.totalPosts === 0) {
                                processedData.totalPosts = result.data[0].total_posts;
                            } else if (selectedType !== 'both') {
                                processedData.totalPosts += result.data[0].total_posts;
                            }
                        }
                    }
                });

                console.log('=== MOTIVATION DATA DEBUG ===');
                console.log('Processed data:', processedData);
                console.log('Leaving reasons:', processedData.leaving?.length || 0);
                console.log('Joining reasons:', processedData.joining?.length || 0);
                console.log('Total posts evaluated:', processedData.totalPosts);
                
                // Log detailed results for debugging
                results.forEach((result, index) => {
                    console.log(`Result ${index + 1} (${result.type}):`, {
                        hasData: !!result.data,
                        dataLength: result.data?.length || 0,
                        error: result.error,
                        totalPosts: result.data?.[0]?.total_posts,
                        sampleData: result.data?.slice(0, 3) || []
                    });
                });
                console.log('===============================');
                
                setReasonsData(processedData);
                
            } catch (err) {
                console.error('Error fetching motivation data:', err);
                setError(err.message);
                setReasonsData({ leaving: [], joining: [] });
            } finally {
                setIsLoading(false);
            }
        };

        fetchMotivationData();
    }, [topicId, startDate, endDate, selectedType]);

    // Process reasons data into histogram format
    const processedChartData = useMemo(() => {
        if (!reasonsData || (!reasonsData.leaving?.length && !reasonsData.joining?.length)) {
            return null;
        }

        // Combine and get all unique reasons for labels
        const allReasons = new Map();
        
        // Process leaving reasons
        reasonsData.leaving?.forEach(item => {
            const normalizedReason = item.value.trim().charAt(0).toUpperCase() + item.value.trim().slice(1).toLowerCase();
            if (!allReasons.has(normalizedReason)) {
                allReasons.set(normalizedReason, { leaving: 0, joining: 0 });
            }
            allReasons.get(normalizedReason).leaving = item.count;
        });
        
        // Process joining reasons
        reasonsData.joining?.forEach(item => {
            const normalizedReason = item.value.trim().charAt(0).toUpperCase() + item.value.trim().slice(1).toLowerCase();
            if (!allReasons.has(normalizedReason)) {
                allReasons.set(normalizedReason, { leaving: 0, joining: 0 });
            }
            allReasons.get(normalizedReason).joining = item.count;
        });

        // Sort by total count (leaving + joining) and take top 15
        const sortedReasons = Array.from(allReasons.entries())
            .sort(([, a], [, b]) => (b.leaving + b.joining) - (a.leaving + a.joining))
            .slice(0, 15);

        if (sortedReasons.length === 0) {
            return null;
        }

        const labels = sortedReasons.map(([reason]) => reason);
        const leavingCounts = sortedReasons.map(([, counts]) => counts.leaving);
        const joiningCounts = sortedReasons.map(([, counts]) => counts.joining);

        const datasets = [];
        
        // Add leaving dataset if we have leaving data
        if (selectedType === 'leaving' || selectedType === 'both') {
            datasets.push({
                label: 'Leaving Reasons',
                data: leavingCounts,
                backgroundColor: 'rgba(220, 53, 69, 0.7)', // Red for leaving
                borderColor: 'rgba(220, 53, 69, 1)',
                borderWidth: 1,
                borderRadius: selectedType === 'both' ? 0 : 4, // No radius when stacked
            });
        }
        
        // Add joining dataset if we have joining data
        if (selectedType === 'joining' || selectedType === 'both') {
            datasets.push({
                label: 'Joining Reasons',
                data: joiningCounts,
                backgroundColor: 'rgba(40, 167, 69, 0.7)', // Green for joining
                borderColor: 'rgba(40, 167, 69, 1)',
                borderWidth: 1,
                borderRadius: selectedType === 'both' ? 0 : 4, // No radius when stacked
            });
        }

        return {
            labels,
            datasets
        };
    }, [reasonsData, selectedType]);

    // Chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: selectedType === 'leaving' ? 'Reasons for Leaving' : 
                      selectedType === 'joining' ? 'Reasons for Joining' : 
                      'Reasons for Leaving and Joining',
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: {
                    bottom: 20
                }
            },
            legend: {
                display: selectedType === 'both', // Show legend when showing both types
                position: 'bottom'
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        return context[0].label;
                    },
                    label: function(context) {
                        const count = context.parsed.y;
                        const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                        const percentage = ((count / total) * 100).toFixed(1);
                        return `${count} mentions (${percentage}%)`;
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Reasons'
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                        size: 11
                    }
                },
                stacked: selectedType === 'both' // Stack x-axis when showing both
            },
            y: {
                title: {
                    display: true,
                    text: 'Number of Mentions'
                },
                beginAtZero: true,
                ticks: {
                    stepSize: 1 // Show integer values only
                },
                stacked: selectedType === 'both' // Stack y-axis when showing both
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    // Render loading state
    if (isLoading) {
        return (
            <div 
                className={className}
                style={{
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    ...style
                }}
            >
                Loading switching reasons...
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div 
                className={className}
                style={{
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#dc3545',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    padding: '20px',
                    textAlign: 'center',
                    ...style
                }}
            >
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        Error loading switching reasons
                    </div>
                    <div style={{ fontSize: '14px' }}>
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    // Render no data state
    if (!processedChartData) {
        return (
            <div 
                className={className}
                style={{
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '16px',
                    ...style
                }}
            >
                No switching reasons data available
                {highlightedPeriod && (
                    <div style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>
                        for {highlightedPeriod}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={className} style={{ ...style }}>
            {highlightedPeriod && (
                <div style={{ 
                    marginBottom: '10px', 
                    fontSize: '14px', 
                    color: '#666',
                    textAlign: 'center',
                    fontWeight: '500'
                }}>
                    Motivation Data for {highlightedPeriod}
                </div>
            )}
            
            {/* Type selector buttons */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '15px',
                gap: '8px'
            }}>
                {['leaving', 'joining', 'both'].map(type => (
                    <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            backgroundColor: selectedType === type ? '#007bff' : '#f8f9fa',
                            color: selectedType === type ? 'white' : '#333',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: selectedType === type ? 'bold' : 'normal',
                            textTransform: 'capitalize'
                        }}
                    >
                        {type}
                    </button>
                ))}
            </div>
            
            <div style={{ height: '300px', width: '100%' }}>
                <Bar data={processedChartData} options={chartOptions} />
            </div>
            <div style={{ 
                marginTop: '10px', 
                fontSize: '12px', 
                color: '#6c757d',
                textAlign: 'center'
            }}>
                Based on {reasonsData.totalPosts || 0} posts evaluated in this period
            </div>
        </div>
    );
};

export default SwitchingReasonsHistogram;